<template>
  <WdsStack direction-column gap-custom="2.5rem" class="tw-py-2">
    <WdsStack direction-column gap-lg>
      <BaseSection>
        <BaseSectionHeader :header-text="$t('Availability')" />

        <BaseSectionBody>
          <BaseFieldGroup
            :label="$t('Start date')"
            :helper-text="$t('When are you ready to hire for this job?')"
          >
            <div class="tw-w-full">
              <WdsSelect
                v-model="selectedStartDateTimeframe"
                :list="startDateTimeframeOptions"
                :placeholder="$t('Unknown')"
                data-test="job-start-date-availability-dropdown"
                name="job-start-date-availability-dropdown"
                :error="getErrorMessage(validationStore, 'startDateTimeframe')"
              />
            </div>
          </BaseFieldGroup>

          <BaseFieldGroup
            v-if="showStartDate"
            :label="$t('Specified date')"
            :helper-text="$t('Which date will this job start?')"
          >
            <div class="tw-w-full">
              <InputDatetimePicker
                v-model="selectedStartDate"
                :placeholder="$t('YYYY-MM-DD')"
                name="date_start"
                format="YYYY-MM-DD"
                :max-date="store.form.endDate"
                data-test="job-start-date"
                :error="getErrorMessage(validationStore, 'startDate')"
              />
            </div>
          </BaseFieldGroup>

          <BaseFieldGroup
            :label="$t('Commitment')"
            :helper-text="$t('What level of commitment do you need?')"
          >
            <div class="tw-w-full">
              <WdsSelect
                v-model="selectedAssociation"
                :list="associationOptions"
                :placeholder="$t('Unknown')"
                data-test="job-type-dropdown"
                name="job-type-dropdown"
                :error="getErrorMessage(validationStore, 'commitment')"
              />
            </div>
          </BaseFieldGroup>

          <BaseFieldGroup
            :label="$t('Length')"
            :helper-text="$t('How long do you expect the initial contract to be?')"
            optional
          >
            <div class="tw-w-full">
              <WdsSelect
                v-model="selectedEndDateTimeframe"
                :list="endDateTimeframeOptions"
                :placeholder="$t('Not sure')"
                data-test="job-length-dropdown"
                name="job-length-dropdown"
                can-deselect
              />
            </div>
          </BaseFieldGroup>

          <BaseFieldGroup
            v-if="showEndDate"
            :label="$t('Finish date')"
            :helper-text="$t('When would this job finish?')"
          >
            <div class="tw-w-full">
              <InputDatetimePicker
                v-model="selectedEndDate"
                float-label
                :placeholder="$t('YYYY-MM-DD')"
                name="date_end"
                format="YYYY-MM-DD"
                :min-date="store.form.startDate"
                data-test="job-end-date"
                :error="getErrorMessage(validationStore, 'endDate')"
              />
            </div>
          </BaseFieldGroup>

          <BaseFieldGroup
            :label="$t('Extension')"
            :helper-text="$t('Is there a possibility to extend or renew this contract?')"
            optional
          >
            <WdsToggle v-model:checked="isExtensionAvailable" data-test="job-contract-extension" />
          </BaseFieldGroup>

          <BaseFieldGroup
            :label="$t('Recruitment')"
            :helper-text="$t('When do you expect to review and reply to applicants?')"
          >
            <div class="tw-w-full">
              <WdsSelect
                v-model="selectedEvaluationPeriod"
                :list="evaluationPeriodOptions"
                :placeholder="$t('Unknown')"
                :error="getErrorMessage(validationStore, 'evaluationPeriod')"
                data-test="job-evaluation-period-dropdown"
                name="job-evaluation-period-dropdown"
              />
            </div>
          </BaseFieldGroup>
        </BaseSectionBody>
      </BaseSection>

      <WdsAlert info>
        <template #default>
          {{ $t('Summary') }}
        </template>

        <template #description>
          {{ availabilitySummary }}
        </template>
      </WdsAlert>
    </WdsStack>

    <Location />
  </WdsStack>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { WdsAlert, WdsSelect, WdsStack, WdsToggle } from '@worksome/wds-components'

import { $t } from '@/composables/translation'
import InputDatetimePicker from '@/components/InputDatetimePicker.vue'
import BaseSection from '@/components/BaseSection.vue'
import BaseSectionHeader from '@/components/BaseSectionHeader.vue'
import BaseSectionBody from '@/components/BaseSectionBody.vue'
import BaseFieldGroup from '@/components/BaseFieldGroup.vue'
import { Association, EvaluationPeriod, JobStartTimeframe, JobEndTimeframe } from '@/gql/graphql'

import useGetSummaryText from '@/components/Job/composables/getSummaryText'
import { getErrorMessage } from '@/components/Job/functions/getErrorMessage'
import { useJobFormStore } from '@/components/Job/Edit/store/jobFormStore'
import { useValidationStore } from '@/components/Job/Edit/store/useValidationStore'
import Location from '@/components/Job/Edit/Tabs/DetailsTab/Location.vue'

const store = useJobFormStore()
const validationStore = useValidationStore()

const showStartDate = computed(() => store.form.startDateTimeframe === JobStartTimeframe.OnDate)
const showEndDate = computed(() => store.form.endDateTimeframe === JobEndTimeframe.OnDate)

const isExtensionAvailable = computed({
  get: () => store.form.isExtensionAvailable,
  set: (checked) => {
    store.form.isExtensionAvailable = checked
  },
})

const selectedAssociation = computed({
  get: () => ({ value: store.form.commitment }),
  set: (selected) => {
    store.form.commitment = selected?.value
  },
})

const selectedEvaluationPeriod = computed({
  get: () => ({ value: store.form.evaluationPeriod }),
  set: (selected) => {
    store.form.evaluationPeriod = selected.value
  },
})

const selectedStartDateTimeframe = computed({
  get: () => ({ value: store.form.startDateTimeframe }),
  set: (selected) => {
    store.form.startDateTimeframe = selected.value
  },
})

const selectedEndDateTimeframe = computed({
  get: () => ({ value: store.form.endDateTimeframe }),
  set: (selected) => {
    store.form.endDateTimeframe = selected.value
  },
})

const selectedStartDate = computed({
  get: () => store.form.startDate ?? '',
  set: (date) => {
    store.form.startDate = date === '' ? undefined : date
  },
})

const selectedEndDate = computed({
  get: () => store.form.endDate ?? '',
  set: (date) => {
    store.form.endDate = date === '' ? undefined : date
  },
})

const startDateTimeframeOptions = [
  { value: JobStartTimeframe.Asap, label: $t('ASAP') },
  { value: JobStartTimeframe.NextMonth, label: $t('Within a Month') },
  { value: JobStartTimeframe.OnDate, label: $t('On a Specific Date') },
]

const associationOptions = [
  { value: Association.FullTime, label: $t('Full-Time Contractor') },
  { value: Association.PartTime, label: $t('Part-Time/Flexible Days') },
  { value: Association.Project, label: $t('One-Time Project') },
]

const endDateTimeframeOptions = [
  { value: JobEndTimeframe.OneMonth, label: $t('1 Month') },
  { value: JobEndTimeframe.ThreeMonths, label: $t('3 Months') },
  { value: JobEndTimeframe.SixMonths, label: $t('6 Months') },
  { value: JobEndTimeframe.OnDate, label: $t('Until Date') },
]

const evaluationPeriodOptions = [
  { value: EvaluationPeriod.Quick, label: $t('Quickly and ongoing') },
  { value: EvaluationPeriod.Week, label: $t('Weekly') },
  { value: EvaluationPeriod.Month, label: $t('Within a month') },
]

const availabilitySummary = useGetSummaryText()
</script>
